// ============================================================================
// The profile's status line and actions (prototype screen 6; TCSD-0369/0370/0373).
// The existing sub page IS the one profile; this is the card the prototype adds:
//   Lead, application not back  → Send the application (their own link, TCSD-0370 ¶2)
//   Bench / Active              → Start paperwork now (same HR card, once; TCSD-0369 ¶2)
//                                 Mark inactive — greyed while any building is assigned
//   Inactive                    → Return to bench, with one required line (TCSD-0373 ¶5)
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { useToast } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { refreshReaders, useReader } from '../../../shared/save-path';
import { DeactivateSubModal } from '../../hr-onboarding/deactivate-sub-modal';
import { subStatusLabel } from '../stages';
import { applicationLinkFor, returnToBench, startPaperwork, workLabel } from './actions';
import { SendApplicationChoice, type SendApplicationTarget } from './send-application';
import { Sheet, bigPrimary, bigSecondary, fieldCls } from './sheet';
import { shortMarkets } from './use-hub-data';

const OPEN_CARD_EXCLUDES = ['Cancelled', 'Complete', 'Completed', 'not_applicable', 'inactive'];

interface State {
  status: string | null;
  markets: string[];
  kind: { does_nightly: boolean; does_projects: boolean; kind_source: string } | null;
  liveBuildings: number;
  openCard: { onboarding_id: string; hr_name: string | null } | null;
  applicationBack: boolean | null;
}

export function ProfileActions({ providerId, name, phone, onChanged }: { providerId: string; name: string; phone: string | null; onChanged: () => void }) {
  const { employee } = useAuth();
  const toast = useToast();
  const [st, setSt] = useState<State | null>(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<null | 'blocked' | 'return' | 'deactivate'>(null);
  const [sendTo, setSendTo] = useState<SendApplicationTarget | null>(null);
  const [why, setWhy] = useState('');
  const [whyErr, setWhyErr] = useState<string | null>(null);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useReader(['service_providers', 'service_assignments', 'partner_onboarding', 'sourcing_prospects'], reload);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [sp, work, asg, cards, app] = await Promise.all([
        supabase.from('service_providers').select('provider_status, coverage_markets').eq('provider_id', providerId).single(),
        supabase.from('v_subcontractor_work').select('does_nightly, does_projects, kind_source').eq('provider_id', providerId).maybeSingle(),
        supabase.from('service_assignments').select('facility_id').eq('provider_id', providerId).in('assignment_status', ['Active', 'Scheduled']),
        supabase.from('partner_onboarding').select('onboarding_id, status, assigned_to_eid, created_at').eq('provider_id', providerId).order('created_at', { ascending: false }),
        supabase.from('sourcing_prospects').select('intake_completed').eq('promoted_provider_id', providerId).order('created_at', { ascending: false }).limit(1),
      ]);
      if (cancelled) return;
      const open = ((cards.data ?? []) as Array<{ onboarding_id: string; status: string | null; assigned_to_eid: string | null }>).find(
        (c) => !OPEN_CARD_EXCLUDES.includes(c.status ?? 'Offer Accepted'),
      );
      let hrName: string | null = null;
      if (open?.assigned_to_eid) {
        const { data: e } = await supabase.from('employees').select('employee_name_first').eq('employee_id', open.assigned_to_eid).maybeSingle();
        hrName = (e as { employee_name_first?: string } | null)?.employee_name_first ?? null;
      }
      if (cancelled) return;
      const spRow = sp.data as { provider_status: string; coverage_markets: string[] | null } | null;
      setSt({
        status: spRow?.provider_status ?? null,
        markets: spRow?.coverage_markets ?? [],
        kind: (work.data as State['kind']) ?? null,
        liveBuildings: new Set(((asg.data ?? []) as Array<{ facility_id: string }>).map((a) => a.facility_id)).size,
        openCard: open ? { onboarding_id: open.onboarding_id, hr_name: hrName } : null,
        applicationBack: app.data && app.data.length ? Boolean((app.data[0] as { intake_completed: boolean }).intake_completed) : null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, tick]);

  if (!st) return null;
  const s = st.status;
  const kindText = !st.kind || st.kind.kind_source === 'unknown'
    ? 'Kind of work not known yet'
    : workLabel(st.kind.does_nightly, st.kind.does_projects) ?? 'Kind of work not known yet';

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(label, { description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-tcs-border bg-tcs-raised p-4" aria-label="Status and actions">
      <div className="mb-3 flex flex-wrap gap-1.5 text-xs">
        <span className="rounded-full bg-tcs-inverse px-2.5 py-0.5 font-medium text-tcs-inverse-ink">{subStatusLabel(s)}</span>
        {st.markets.map((m) => (
          <span key={m} className="rounded-full border border-tcs-border px-2.5 py-0.5 text-tcs-text-secondary">{shortMarkets([m])}</span>
        ))}
        <span className="rounded-full border border-tcs-border px-2.5 py-0.5 text-tcs-text-secondary">{kindText}</span>
      </div>

      {s === 'Lead' && st.applicationBack !== true && (
        <button
          type="button"
          disabled={busy}
          className={bigSecondary}
          onClick={() =>
            void run('Could not create the application link', async () => {
              const link = await applicationLinkFor(providerId);
              setSendTo({ name, phone, link });
            })
          }
        >
          Send the application
        </button>
      )}

      {(s === 'Ready' || s === 'Active') &&
        (st.openCard ? (
          <div className="mt-2 w-full rounded-lg border border-tcs-border px-4 py-2.5 text-center text-sm text-tcs-text-secondary">
            Paperwork started · {st.openCard.hr_name ?? 'HR'} notified
          </div>
        ) : (
          <button
            type="button"
            disabled={busy}
            className={bigSecondary}
            onClick={() =>
              void run('Could not start the paperwork', async () => {
                const out = await startPaperwork(providerId);
                toast.success(out.created ? 'Paperwork started — HR has been notified' : 'Paperwork was already started', { description: out.message });
                refreshReaders(['partner_onboarding']);
                reload();
              })
            }
          >
            Start paperwork now
          </button>
        ))}

      {(s === 'Ready' || s === 'Active' || s === 'Pending' || s === 'Onboarding') &&
        (st.liveBuildings > 0 ? (
          <button type="button" aria-disabled="true" className={`${bigSecondary} opacity-50`} onClick={() => setSheet('blocked')}>
            Mark inactive — {st.liveBuildings} building{st.liveBuildings === 1 ? '' : 's'} still assigned
          </button>
        ) : (
          <button type="button" disabled={busy} className={bigSecondary} onClick={() => setSheet('deactivate')}>
            Mark inactive
          </button>
        ))}

      {s === 'Inactive' &&
        (st.kind && st.kind.kind_source !== 'unknown' && !st.kind.does_nightly ? (
          <p className="mt-2 text-sm text-tcs-text-secondary">
            Project work — the bench is for nightly cleaners, so they come back by being put on project work.
          </p>
        ) : (
          <button type="button" className={bigPrimary} onClick={() => setSheet('return')}>
            Return to bench
          </button>
        ))}

      {sendTo && (
        <Sheet title={`Send ${sendTo.name} the application`} label="Send the application" onClose={() => setSendTo(null)}>
          <SendApplicationChoice target={sendTo} onDone={() => setSendTo(null)} />
        </Sheet>
      )}
      {sheet === 'blocked' && (
        <Sheet title="Can't mark inactive yet" label="Can't mark inactive yet" onClose={() => setSheet(null)}>
          <p className="text-sm text-tcs-text">
            {name} still has {st.liveBuildings} building{st.liveBuildings === 1 ? '' : 's'}. Move them first — from each building's sub
            change — then this unlocks. The app will never let a building quietly lose its sub.
          </p>
          <button type="button" className={bigSecondary} onClick={() => setSheet(null)}>
            OK
          </button>
        </Sheet>
      )}
      {sheet === 'deactivate' && (
        <DeactivateSubModal
          providerId={providerId}
          providerName={name}
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            refreshReaders(['service_providers']);
            reload();
            onChanged();
          }}
        />
      )}
      {sheet === 'return' && (
        <Sheet title={`Return ${name} to the bench`} label="Return to bench" onClose={() => setSheet(null)}>
          <p className="mb-2 text-sm text-tcs-text-secondary">One line on why they're coming back:</p>
          <input
            aria-label="Why they are coming back"
            value={why}
            onChange={(e) => {
              setWhy(e.target.value);
              setWhyErr(null);
            }}
            placeholder="e.g. New crew lead; Jose vouches for them"
            className={fieldCls}
          />
          {whyErr && <p className="mt-1 text-sm text-st-danger-fg">{whyErr}</p>}
          <button
            type="button"
            disabled={busy}
            className={bigPrimary}
            onClick={() => {
              if (!why.trim()) {
                setWhyErr('One line on why, please.');
                return;
              }
              void run('Could not return them to the bench', async () => {
                const who = employee ? `${employee.employee_name_first ?? ''} ${employee.employee_name_last ?? ''}`.trim() : null;
                await returnToBench(providerId, why, who);
                toast.success(`${name} is back on the bench`, { description: `“${why.trim()}”` });
                setSheet(null);
                setWhy('');
                reload();
                onChanged();
              });
            }}
          >
            Return to bench
          </button>
        </Sheet>
      )}
    </section>
  );
}
